import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { IoMdArrowBack } from "react-icons/io";
import { FiFileText, FiCheckCircle, FiShield, FiUser, FiXCircle, FiDollarSign, FiSend, FiAlertTriangle } from "react-icons/fi";
import * as api from "../../api/index";

const Othercontract = () => {
  const { id } = useParams(); // Catches the unique URL ID
  const navigate = useNavigate();
  const location = useLocation();

  const [contractData, setContractData] = useState(null);
  const [role, setRole] = useState("");
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  // 1. Fetch live contract data from backend on page load using unique ID
  useEffect(() => {
    const fetchContract = async () => {
      try {
        setLoading(true);
        const res = await api.getContractByUniqueId(id);
        setContractData(res.data.contract);
        setRole(res.data.role);
      } catch (err) {
        console.error("Failed to load contract:", err);
        setErrorMessage(err.response?.data?.message || "Invalid or expired contract link.");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchContract();
    } else if (location.state) {
      // Fallback if navigated with state directly
      setContractData({
        _id: location.state?.contractId,
        itemTitle: location.state?.itemTitle || "Item / Service",
        itemDescription: location.state?.itemDescription || "",
        price: location.state?.price || "0",
        myName: location.state?.myName || "",
        otherPartyName: location.state?.otherPartyName || "",
        myEmail: location.state?.myEmail || "",
        otherPartyEmail: location.state?.otherPartyEmail || "",
        status: location.state?.status || "delivered"
      });
      setLoading(false);
    } else {
      setLoading(false);
      setErrorMessage("No contract identifier found.");
    }
  }, [id, location.state]);

  if (loading) {
    return (
      <div className="bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="text-center space-y-3">
          <div className="w-10 h-10 border-4 border-orange-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="text-xs font-black text-gray-400 uppercase tracking-widest">Loading live contract...</p>
        </div>
      </div>
    );
  }

  if (!contractData) {
    return (
      <div className="bg-gray-50 min-h-screen flex items-center justify-center p-6">
        <div className="bg-white p-8 rounded-[2.5rem] shadow-xl text-center space-y-4 max-w-sm w-full border border-gray-100">
          <FiAlertTriangle size={32} className="text-red-500 mx-auto" />
          <h2 className="text-lg font-black text-gray-900">Contract Not Found</h2>
          <p className="text-xs text-gray-500 font-medium">{errorMessage || "The requested contract could not be retrieved."}</p>
        </div>
      </div>
    );
  }

  const currentStatus = contractData.status;

  const handleSubmitDelivery = async () => {
    setIsSubmitting(true);
    setErrorMessage("");
    try {
      const res = await api.submitDeliveryContract(contractData._id);
      if (res.data.success) {
        // Navigate directly to Otherstate passing status and itemTitle
        navigate("/contract/status", {
          state: {
            status: "submitted",
            itemTitle: contractData.itemTitle
          }
        });
      }
    } catch (err) {
      setErrorMessage(err.response?.data?.message || "Failed to submit delivery confirmation.");
      setIsSubmitting(false);
    }
  };

  const handleConfirmReject = async (e) => {
    e.preventDefault();
    setShowRejectModal(false);
    setIsSubmitting(true);
    setErrorMessage("");
    try {
      const res = await api.rejectContract(contractData._id, { rejectReason });
      if (res.data.success) {
        // Navigate directly to Otherstate passing status and itemTitle
        navigate("/contract/status", {
          state: {
            status: "rejected",
            itemTitle: contractData.itemTitle
          }
        });
      }
    } catch (err) {
      setErrorMessage(err.response?.data?.message || "Failed to reject contract.");
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen pb-12 relative">
      {/* 🔝 Sticky Header matching app design style */}
      <div className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b px-4 py-4 flex items-center justify-between">
        <button
          onClick={() => navigate(-1)}
          disabled={isSubmitting}
          className="p-2 hover:bg-gray-100 rounded-full transition text-gray-700 flex items-center gap-1 disabled:opacity-50"
        >
          <IoMdArrowBack size={24} />
          <span className="text-[10px] font-black uppercase tracking-widest">Back</span>
        </button>
        <h1 className="text-lg font-black text-gray-900 tracking-tight">Contract Review</h1>
        <button 
          onClick={() => alert("Contract delivery status is securely logged with OurASAP.")}
          className="bg-orange-600 hover:bg-orange-700 text-white text-[10px] font-black uppercase tracking-widest px-4 py-2.5 rounded-xl transition shadow-md shadow-orange-100"
        >
          OurASAP Secure
        </button>
      </div>

      <div className="max-w-xl mx-auto px-6 pt-8">
        {errorMessage && (
          <div className="bg-red-50 border-2 border-red-200 text-red-700 p-4 rounded-2xl text-xs font-bold mb-6">
            {errorMessage}
          </div>
        )}
        
        {/* 🛡️ Status Banners */}
        {currentStatus === "active"&& (
          <div className="bg-teal-900 rounded-[2.5rem] p-6 mb-8 text-white shadow-xl shadow-teal-100 overflow-hidden relative">
            <div className="absolute right-[-10%] top-[-20%] w-32 h-32 bg-white/10 rounded-full blur-3xl"></div>
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-4">
                 <div className="p-2 bg-teal-800 rounded-xl"><FiShield className="text-teal-100" size={20} /></div>
                 <h2 className="text-sm font-black uppercase tracking-widest">Delivery Confirmation Required</h2>
              </div>
              <p className="text-xs font-bold text-teal-50 leading-relaxed">
                You have marked this service/item as delivered. Submit your final completion notice below to notify {contractData.otherPartyName}, or reject if adjustments are needed.
              </p>
            </div>
          </div>
        )}

        {currentStatus === "submitted" && (
          <div className="bg-green-800 rounded-[2.5rem] p-6 mb-8 text-white shadow-xl shadow-green-100">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-green-700 rounded-xl"><FiCheckCircle className="text-white" size={20} /></div>
              <h2 className="text-sm font-black uppercase tracking-widest">Delivery Submitted Successfully</h2>
            </div>
            <p className="text-xs font-medium text-green-50">
              Your completion notice has been sent to the buyer for final verification and escrow release.
            </p>
          </div>
        )}

        {currentStatus === "rejected" && (
          <div className="bg-red-800 rounded-[2.5rem] p-6 mb-8 text-white shadow-xl shadow-red-100">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-red-700 rounded-xl"><FiXCircle className="text-white" size={20} /></div>
              <h2 className="text-sm font-black uppercase tracking-widest">Contract Rejected</h2>
            </div>
            <p className="text-xs font-medium text-red-50">
              This contract's delivery milestone was rejected. Feedback has been logged for review.
            </p>
          </div>
        )}

        {/* 📝 Contract Summary Details Card */}
        <div className="space-y-6">
          {currentStatus === "delivered" && (
            <div className="flex gap-4 pt-2">
              <button
                type="button"
                disabled={isSubmitting}
                onClick={() => setShowRejectModal(true)}
                className="flex-1 py-5 bg-white border-2 border-red-100 hover:bg-red-50 text-red-600 rounded-[1.5rem] font-black uppercase tracking-[0.15em] text-xs transition-all shadow-md active:scale-95 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FiXCircle size={18} />
                {isSubmitting ? "Processing..." : "Reject"}
              </button>
              
              <button
                type="button"
                disabled={isSubmitting}
                onClick={handleSubmitDelivery}
                className="flex-1 py-5 bg-green-600 hover:bg-green-700 text-white rounded-[1.5rem] font-black uppercase tracking-[0.15em] text-xs transition-all shadow-xl shadow-green-200 active:scale-95 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FiSend size={18} />
                {isSubmitting ? "Submitting..." : "Submit"}
              </button>
            </div>
          )}
        
          <div className="bg-white p-8 rounded-[2.5rem] shadow-xl shadow-gray-200/50 border border-gray-100 space-y-6">
            <h3 className="text-xl font-black text-gray-900 flex items-center gap-2">
              <FiFileText className="text-orange-600" /> Deal Terms & Delivery
            </h3>

            {/* Item Title */}
            <div className="space-y-1">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Item / Service Name</label>
              <div className="w-full bg-gray-50 border-2 border-gray-100 rounded-2xl py-4 px-5 font-bold text-gray-900">
                {contractData.itemTitle}
              </div>
            </div>

            {/* Description */}
            <div className="space-y-1">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Delivery Conditions / Notes</label>
              <div className="w-full bg-gray-50 border-2 border-gray-100 rounded-2xl py-4 px-5 font-bold text-gray-900 min-h-[90px] whitespace-pre-wrap text-sm">
                {contractData.itemDescription}
              </div>
            </div>

            {/* Price */}
            <div className="space-y-1">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Agreed Amount</label>
              <div className="w-full bg-gray-50 border-2 border-gray-100 rounded-2xl py-4 px-5 font-bold text-gray-900 flex items-center gap-2 text-lg text-orange-600">
                <FiDollarSign size={18} />
                {Number(contractData.price).toLocaleString()} ₦
              </div>
            </div>
          </div>

          {/* Parties Involved Card */}
          <div className="bg-white p-8 rounded-[2.5rem] shadow-xl shadow-gray-200/50 border border-gray-100 space-y-6">
            <h3 className="text-xl font-black text-gray-900 flex items-center gap-2">
              <FiUser className="text-orange-600" /> Parties Involved
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-gray-50 p-4 rounded-2xl border border-gray-100">
                <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest block mb-1">Creator / Seller</span>
                <p className="font-extrabold text-gray-900 text-sm">{contractData.myName}</p>
                <p className="text-xs text-gray-500 font-medium truncate mt-0.5">{contractData.myEmail}</p>
              </div>

              <div className="bg-gray-50 p-4 rounded-2xl border border-gray-100">
                <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest block mb-1">Counterparty / Buyer</span>
                <p className="font-extrabold text-gray-900 text-sm">{contractData.otherPartyName}</p>
                <p className="text-xs text-gray-500 font-medium truncate mt-0.5">{contractData.otherPartyEmail}</p>
              </div>
            </div>
          </div>
        </div>

        <p className="text-center mt-10 text-[10px] font-black text-gray-300 uppercase tracking-widest">
          Secure Contracts • Instant Enforcement • OurASAP
        </p>
      </div>

      {/* ⚠️ Reject Popup Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <form onSubmit={handleConfirmReject} className="bg-white rounded-[2.5rem] p-8 max-w-sm w-full shadow-2xl border border-gray-100 space-y-6 animate-in fade-in zoom-in duration-200">
            <div className="w-16 h-16 bg-red-50 text-red-600 rounded-3xl mx-auto flex items-center justify-center shadow-inner">
              <FiAlertTriangle size={32} />
            </div>
            
            <div className="space-y-2 text-center">
              <h3 className="text-xl font-black text-gray-900">Reject Delivery?</h3>
              <p className="text-xs font-bold text-gray-500 leading-relaxed">
                Please provide a brief reason for rejecting or updating this contract stage.
              </p>
            </div>

            <div className="space-y-2">
              <textarea
                required
                rows="3"
                disabled={isSubmitting}
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Enter rejection reason or requested changes..."
                className="w-full bg-gray-50 border-2 border-gray-100 focus:border-red-500 focus:bg-white rounded-2xl p-4 font-bold text-xs text-gray-900 transition-all outline-none resize-none disabled:opacity-50"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                disabled={isSubmitting}
                onClick={() => setShowRejectModal(false)}
                className="flex-1 py-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-2xl font-black uppercase tracking-widest text-[10px] transition active:scale-95 disabled:opacity-50"
              >
                Close 
              </button> 
              <button 
                type="submit"
                disabled={isSubmitting}
                className="flex-1 py-4 bg-red-600 hover:bg-red-700 text-white rounded-2xl font-black uppercase tracking-widest text-[10px] transition shadow-lg shadow-red-100 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? "Processing..." : "Confirm Reject"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default Othercontract;